import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./YogaStage.css";

function displayForAnswer(answer) {
  if (answer === "Childs Pose") return "Child's Pose";
  return answer;
}

function useMatchMedia(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = () => setMatches(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

export default function YogaStage({ data, onDone, onSessionPointsChange }) {
  const slides = data.slides;
  const bankChips = data.bankChips;
  const carouselRef = useRef(null);
  const scrollEndTimer = useRef(null);
  const suppressClickRef = useRef(false);
  const pointerDragStartRef = useRef({ x: 0, y: 0 });
  const movedEnoughRef = useRef(false);
  /** Sync ref so pointermove runs before React re-renders after pointerdown. */
  const activePointerDragRef = useRef(null);

  const isMobileLayout = useMatchMedia("(max-width: 768px)");
  const isCoarsePointer = useMatchMedia("(pointer: coarse)");

  const slidePanels = useMemo(() => {
    if (!isMobileLayout) {
      return [slides.slice(0, 4), slides.slice(4, 8)];
    }
    return slides.map((slide) => [slide]);
  }, [slides, isMobileLayout]);

  const totalPanels = slidePanels.length;

  const [filled, setFilled] = useState({});
  const [usedMatches, setUsedMatches] = useState(() => new Set());
  const [picked, setPicked] = useState(null);
  const [dragMatch, setDragMatch] = useState(null);
  const [panelIndex, setPanelIndex] = useState(0);
  /** Touch / pointer drag ghost (when native HTML5 DnD is off). */
  const [pointerDrag, setPointerDrag] = useState(null);
  const doneTimerRef = useRef(null);

  const correctCount = useMemo(() => Object.keys(filled).length, [filled]);
  const pointsPerMatch = data.pointsPerMatch ?? 5;
  const targetMatches = slides.length;
  const currentScore = correctCount * pointsPerMatch;

  useEffect(() => {
    onSessionPointsChange?.(currentScore);
  }, [currentScore, onSessionPointsChange]);

  const placeMatch = useCallback((slotIndex, answer, match) => {
    if (match !== answer) return false;
    setFilled((prev) => ({ ...prev, [slotIndex]: match }));
    setUsedMatches((prev) => new Set(prev).add(match));
    setPicked(null);
    setDragMatch(null);
    return true;
  }, []);

  const onChipPointerPick = (match) => {
    setPicked((p) => (p === match ? null : match));
  };

  const onZoneClick = (index, answer) => {
    if (!picked) return;
    placeMatch(index, answer, picked);
  };

  /** Mouse + fine pointer on wide layout: native drag. Otherwise pointer-drag or tap. */
  const allowNativeDrag = !isMobileLayout && !isCoarsePointer;

  const onDragStartChip = (e, match) => {
    if (!allowNativeDrag) {
      e.preventDefault();
      return;
    }
    setDragMatch(match);
    e.dataTransfer.setData("text/plain", match);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragEndChip = () => {
    setDragMatch(null);
  };

  const onDragOverZone = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDropZone = (e, index, answer) => {
    e.preventDefault();
    const match = e.dataTransfer.getData("text/plain") || dragMatch;
    if (!match) return;
    placeMatch(index, answer, match);
  };

  const handleChipPointerDown = (e, chip, used) => {
    if (used || allowNativeDrag) return;
    e.preventDefault();
    pointerDragStartRef.current = { x: e.clientX, y: e.clientY };
    movedEnoughRef.current = false;
    const session = {
      match: chip.match,
      label: chip.label,
      x: e.clientX,
      y: e.clientY,
    };
    activePointerDragRef.current = session;
    setPointerDrag(session);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
  };

  const handleChipPointerMove = (e) => {
    if (!activePointerDragRef.current) return;
    const dx = e.clientX - pointerDragStartRef.current.x;
    const dy = e.clientY - pointerDragStartRef.current.y;
    if (Math.hypot(dx, dy) > 8) movedEnoughRef.current = true;
    const next = { ...activePointerDragRef.current, x: e.clientX, y: e.clientY };
    activePointerDragRef.current = next;
    setPointerDrag(next);
  };

  const handleChipPointerUp = (e) => {
    if (!activePointerDragRef.current || allowNativeDrag) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    const match = activePointerDragRef.current.match;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const zone = el?.closest?.("[data-yoga-slot]");
    if (zone) {
      const slot = parseInt(zone.getAttribute("data-yoga-slot") || "0", 10);
      const answer = zone.getAttribute("data-yoga-answer") || "";
      placeMatch(slot, answer, match);
    }
    if (movedEnoughRef.current) {
      suppressClickRef.current = true;
    }
    movedEnoughRef.current = false;
    activePointerDragRef.current = null;
    setPointerDrag(null);
  };

  const handleChipPointerCancel = (e) => {
    if (!activePointerDragRef.current || allowNativeDrag) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    activePointerDragRef.current = null;
    setPointerDrag(null);
  };

  const handleChipClick = (chip, used) => {
    if (used) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onChipPointerPick(chip.match);
  };

  const finishedRef = useRef(false);
  const doneTriggeredRef = useRef(false);
  useEffect(() => {
    if (correctCount < targetMatches || finishedRef.current) return undefined;
    finishedRef.current = true;
    doneTimerRef.current = setTimeout(() => {
      if (doneTriggeredRef.current) return;
      doneTriggeredRef.current = true;
      onDone(currentScore);
    }, 700);
    return undefined;
  }, [correctCount, targetMatches, currentScore, onDone]);

  useEffect(
    () => () => {
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    },
    []
  );

  useLayoutEffect(() => {
    const el = carouselRef.current;
    if (el) el.scrollLeft = 0;
    setPanelIndex(0);
  }, [isMobileLayout]);

  const syncPanelFromScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w <= 0) return;
    const idx = Math.round(el.scrollLeft / w);
    setPanelIndex(Math.min(totalPanels - 1, Math.max(0, idx)));
  }, [totalPanels]);

  const onCarouselScroll = () => {
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      syncPanelFromScroll();
    }, 80);
  };

  const scrollToPanel = (idx) => {
    const el = carouselRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const clamped = Math.min(totalPanels - 1, Math.max(0, idx));
    el.scrollTo({ left: clamped * w, behavior: "smooth" });
    setPanelIndex(clamped);
  };

  const goPrev = () => scrollToPanel(panelIndex - 1);
  const goNext = () => scrollToPanel(panelIndex + 1);

  const globalIndexFor = (panelIdx, localIdx) => {
    if (!isMobileLayout) {
      return panelIdx === 0 ? localIdx : localIdx + 4;
    }
    return panelIdx + localIdx;
  };

  const renderSlide = (slide, index) => {
    const isFilled = filled[index] != null;
    return (
      <div className="yoga-item" key={`${slide.image}-${index}`}>
        <img src={slide.image} alt="" draggable={false} />
        <div
          role="button"
          tabIndex={0}
          data-yoga-slot={index}
          data-yoga-answer={slide.answer}
          className={`yoga-drop${isFilled ? " is-filled" : ""}${picked ? " is-target" : ""}`}
          onClick={() => onZoneClick(index, slide.answer)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onZoneClick(index, slide.answer);
            }
          }}
          onDragOver={onDragOverZone}
          onDrop={(e) => onDropZone(e, index, slide.answer)}
        >
          {isFilled ? displayForAnswer(filled[index]) : ""}
        </div>
      </div>
    );
  };

  const showArrows = totalPanels > 1;

  return (
    <div className="unhealthy-stage yoga-stage">
      {pointerDrag ? (
        <div
          className="yoga-drag-ghost"
          style={{ left: pointerDrag.x, top: pointerDrag.y }}
          aria-hidden
        >
          {pointerDrag.label}
        </div>
      ) : null}

      <div className="yoga-stage-inner">
        <div className="yoga-head-text">
          <h1>
            {data.headlineLine1}
            <br />
            {data.headlineLine2}
          </h1>
          <p>{data.subhead}</p>
        </div>

        <p className="yoga-touch-hint">
          {allowNativeDrag
            ? "Drag a pose name into the matching box, or click a name then click a box."
            : "Drag a pose name into a box, or tap a name then tap the matching box."}
        </p>

        <div className="yoga-carousel-outer">
          {showArrows ? (
            <button
              type="button"
              className="yoga-arrow yoga-arrow--prev"
              aria-label="Previous poses"
              disabled={panelIndex <= 0}
              onClick={goPrev}
            >
              ‹
            </button>
          ) : null}
          {showArrows ? (
            <button
              type="button"
              className="yoga-arrow yoga-arrow--next"
              aria-label="Next poses"
              disabled={panelIndex >= totalPanels - 1}
              onClick={goNext}
            >
              ›
            </button>
          ) : null}

          <div ref={carouselRef} className="yoga-carousel" onScroll={onCarouselScroll}>
            {slidePanels.map((chunk, pi) => (
              <section
                key={`panel-${pi}`}
                className={`yoga-slide-panel${isMobileLayout ? " yoga-slide-panel--single" : ""}`}
                aria-label={isMobileLayout ? `Pose ${pi + 1}` : `Panel ${pi + 1}`}
              >
                {chunk.map((s, localIdx) => renderSlide(s, globalIndexFor(pi, localIdx)))}
              </section>
            ))}
          </div>
        </div>

        <div className="yoga-bank-wrap">
          <div className="yoga-bank-grid">
            {bankChips.map((chip, idx) => {
              const used = usedMatches.has(chip.match);
              return (
                <button
                  key={`${chip.match}-${idx}`}
                  type="button"
                  className={`yoga-chip${used ? " is-used" : ""}${picked === chip.match ? " is-picked" : ""}${pointerDrag?.match === chip.match ? " is-dragging" : ""}`}
                  draggable={allowNativeDrag && !used}
                  onClick={() => handleChipClick(chip, used)}
                  onDragStart={(e) => !used && onDragStartChip(e, chip.match)}
                  onDragEnd={onDragEndChip}
                  onPointerDown={(e) => handleChipPointerDown(e, chip, used)}
                  onPointerMove={!allowNativeDrag ? handleChipPointerMove : undefined}
                  onPointerUp={handleChipPointerUp}
                  onPointerCancel={handleChipPointerCancel}
                  style={!allowNativeDrag ? { touchAction: "none" } : undefined}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
